/**
 * Life Blueprint V2.9.9 — Migration Batch 04 (Sheet27: B51 / B52 / B53 / B54 / B55)
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * STRICT SCOPE
 *   Implements ONLY Sheet27!B51, B52, B53, B54, B55. Not B48/B49/B50, not
 *   B56+. Not wired into the main V2.9.9 pipeline (still throws).
 *
 * GOLDEN MASTER PROVENANCE (audited)
 *
 *   Sheet27!B51:
 *     =IF( Sheet39!$B$65 <> <guard-token>,
 *          <error/status-text>,               // NOT migrated — no clean evidence
 *          MOD($B$14 - Sheet39!$B$63, 60) + 1 )
 *
 *     Guard token audited via VBA AscW: 24050 -25972 -29759 32 9989
 *       → U+5DF2  已
 *       → U+9A8C  验    ( -25972 + 65536 = 39564 = 0x9A8C )
 *       → U+8BC1  证    ( -29759 + 65536 = 35777 = 0x8BC1 )
 *       → U+0020  space
 *       → U+2705  ✅
 *     → "已验证 ✅"    (5 characters; NO semantic label attached)
 *
 *     Audited: Sheet39!$B$65 === guard-token → TRUE, so the formula takes
 *     the normal MOD branch. For the audited sample:
 *       B14 = 28160, Sheet39!$B$63 = 30713
 *       excelMod(28160 - 30713, 60) + 1 = excelMod(-2553, 60) + 1
 *                                       = 27 + 1 = 28
 *
 *   Sheet27!B52:
 *     =IF( '01_个人资料'!$C$32 = "手动覆盖",
 *          IF( '01_个人资料'!$I$20 <> "",
 *              LEFT('01_个人资料'!$I$20, 1),
 *              IF( '01_个人资料'!$I$22 <> "", '01_个人资料'!$I$22, "" ) ),
 *          IFERROR( VLOOKUP(B51, Sheet39!$A:$F, 3, FALSE()), "" ) )
 *
 *   Sheet27!B53:
 *     =IFERROR( VLOOKUP(B51, Sheet39!$A:$F, 4, FALSE()), "" )
 *
 *   Sheet27!B54:
 *     =IF( '01_个人资料'!$C$32 = "手动覆盖",
 *          IF( '01_个人资料'!$I$20 <> "",
 *              RIGHT('01_个人资料'!$I$20, 1),
 *              "" ),
 *          IFERROR( VLOOKUP(B51, Sheet39!$A:$F, 5, FALSE()), "" ) )
 *
 *   Sheet27!B55:
 *     = B52 & B54
 *
 *   Audited current Golden Master (automatic branch, C32 = "自动排盘"):
 *     B51 = 28
 *     Sheet39 row 29: A29=28, C29=辛 (U+8F9B), D29=8, E29=卯 (U+536F)
 *     B52 = 辛, B53 = 8, B54 = 卯, B55 = 辛卯
 *
 * KEY BEHAVIOURAL DISTINCTIONS (do NOT normalise)
 *
 *   • Batch 03 (B45/B46) uses `VLOOKUP(..., FALSE)` with NO surrounding
 *     IFERROR. Its migration contract intentionally throws
 *     `MISSING_REFERENCE_EVIDENCE` on missing keys because Sheet40/Sheet41
 *     are `PARTIAL_GOLDEN_MASTER_REFERENCE_EVIDENCE` and guessing is
 *     forbidden.
 *
 *   • Batch 04 (B52/B53/B54) uses `IFERROR(VLOOKUP(..., FALSE), "")`. The
 *     audited Excel behaviour on lookup failure is to return the empty
 *     string. That is faithfully preserved here. This is NOT a silent
 *     "no-evidence" fallback — it is the audited Excel semantics.
 *
 *   Both behaviours are correct for their respective formulas. Do NOT
 *   collapse them into a single rule.
 *
 * REFERENCE DATA
 *   Sheet39 evidence in this batch is
 *   `PARTIAL_GOLDEN_MASTER_REFERENCE_EVIDENCE` — only the cells and row
 *   required by the current Golden Master sample have been migrated. M02
 *   MUST NOT be flipped to IMPLEMENTED because two cells (B63, B65) and one
 *   lookup row (A29:F29) have been captured.
 *
 * DETERMINISM
 *   No randomness. Given identical input, byte-wise identical output.
 * ─────────────────────────────────────────────────────────────────────────────
 */

import { excelMod } from "@/engine/v299/batch01_sheet27";
import { MANUAL_MODE_TOKEN } from "@/engine/v299/batch03_sheet27";

/**
 * Audited Sheet39!$B$65 guard token, preserved as raw Unicode with NO
 * semantic label. Derived exactly from AscW evidence: 24050 -25972 -29759
 * 32 9989 → 0x5DF2 0x9A8C 0x8BC1 0x0020 0x2705.
 */
export const B51_GUARD_TOKEN = "\u5DF2\u9A8C\u8BC1\u0020\u2705";

/**
 * Partial Sheet39 reference evidence for the current Golden Master sample.
 * NOT a complete Sheet39 migration.
 */
export const PARTIAL_SHEET39_REFERENCE_EVIDENCE = Object.freeze({
  codeName: "Sheet39",
  worksheetIndex: 39,
  workbookSheet: "DB_60甲子",
  status: "PARTIAL_GOLDEN_MASTER_REFERENCE_EVIDENCE",
  note:
    "Only the cells (B63, B65) and the single lookup row (A29:F29) required " +
    "by the current Golden Master sample have been audited. This is NOT a " +
    "complete Sheet39 / DB_60甲子 reference table. M02 must remain " +
    "NOT_STARTED.",
  cells: Object.freeze({
    B63: 30713,
    B65: "\u5DF2\u9A8C\u8BC1\u0020\u2705", // audited guard token
  }),
  // Two-column-shape rows keyed by Excel column letter. Columns not present
  // in the audited row are intentionally absent — they were not audited.
  rows: Object.freeze([
    Object.freeze({
      a: 28,
      c: "\u8F9B", // 辛 (U+8F9B)
      d: 8,
      e: "\u536F", // 卯 (U+536F)
      sourceRow: "Sheet39!A29:F29",
    }),
  ]),
});

function isNonEmptyString(v) {
  return typeof v === "string" && v.length > 0;
}

/**
 * Sheet27!B51.
 *
 * @param {{
 *   upstreamValue_B14: number,
 *   sheet39Ref?: typeof PARTIAL_SHEET39_REFERENCE_EVIDENCE
 * }} slice
 * @returns {number}
 */
export function computeB51(slice) {
  const sheet39Ref = slice.sheet39Ref || PARTIAL_SHEET39_REFERENCE_EVIDENCE;
  const { upstreamValue_B14 } = slice;
  if (!Number.isInteger(upstreamValue_B14)) {
    throw new TypeError("[Batch04·B51] upstreamValue_B14 must be an integer");
  }
  if (!sheet39Ref || !sheet39Ref.cells) {
    throw new TypeError("[Batch04·B51] sheet39Ref.cells required");
  }
  const guard = sheet39Ref.cells.B65;
  if (guard !== B51_GUARD_TOKEN) {
    // Excel formula's non-normal branch text has NOT been audited. Throwing
    // a labelled error is the audited-safe response — never invent text.
    const err = new Error(
      "[Batch04·B51] Sheet39!B65 does not match the audited guard token; " +
        "the non-normal branch text has NOT been migrated (no audited " +
        "evidence). Refusing to guess."
    );
    err.code = "GUARD_FAILURE";
    err.expectedTokenCodePoints = [0x5DF2, 0x9A8C, 0x8BC1, 0x0020, 0x2705];
    err.actual = guard;
    throw err;
  }
  const B63 = sheet39Ref.cells.B63;
  if (!Number.isInteger(B63)) {
    throw new TypeError("[Batch04·B51] sheet39Ref.cells.B63 must be an integer");
  }
  return excelMod(upstreamValue_B14 - B63, 60) + 1;
}

/**
 * Excel-compatible exact-match VLOOKUP over Sheet39 with IFERROR(...,"").
 * On missing key, returns the empty string — this is the audited Excel
 * behaviour, not a "no-evidence" fallback. Do NOT rename or collapse.
 *
 * `column` is the JS-object key that mirrors the Excel column letter
 * (`"c"`, `"d"`, `"e"` for columns 3, 4, 5).
 */
function vlookupSheet39WithIferror(key, column, sheet39Ref) {
  const row = sheet39Ref.rows.find((r) => r.a === key);
  if (!row) return ""; // Excel: IFERROR(VLOOKUP(...), "")
  if (!(column in row)) {
    // The requested column was not present in the audited row — treat as
    // missing lookup, preserving the IFERROR("") behaviour.
    return "";
  }
  return row[column];
}

/**
 * Sheet27!B52.
 * @param {Object} slice
 * @returns {string|number}
 */
export function computeB52(slice) {
  const sheet39Ref = slice.sheet39Ref || PARTIAL_SHEET39_REFERENCE_EVIDENCE;
  const { upstreamC32, upstreamI20, upstreamI22, upstreamValue_B51 } = slice;
  if (upstreamC32 === MANUAL_MODE_TOKEN) {
    if (isNonEmptyString(upstreamI20)) return upstreamI20.substring(0, 1); // LEFT(I20,1)
    if (isNonEmptyString(upstreamI22)) return upstreamI22;                 // whole I22
    return "";
  }
  if (!Number.isInteger(upstreamValue_B51)) {
    throw new TypeError(
      "[Batch04·B52] upstreamValue_B51 must be an integer in the automatic branch"
    );
  }
  return vlookupSheet39WithIferror(upstreamValue_B51, "c", sheet39Ref);
}

/**
 * Sheet27!B53. Automatic branch only (no manual override in the audited formula).
 * @param {Object} slice
 * @returns {string|number}
 */
export function computeB53(slice) {
  const sheet39Ref = slice.sheet39Ref || PARTIAL_SHEET39_REFERENCE_EVIDENCE;
  const { upstreamValue_B51 } = slice;
  if (!Number.isInteger(upstreamValue_B51)) {
    throw new TypeError("[Batch04·B53] upstreamValue_B51 must be an integer");
  }
  return vlookupSheet39WithIferror(upstreamValue_B51, "d", sheet39Ref);
}

/**
 * Sheet27!B54.
 * @param {Object} slice
 * @returns {string|number}
 */
export function computeB54(slice) {
  const sheet39Ref = slice.sheet39Ref || PARTIAL_SHEET39_REFERENCE_EVIDENCE;
  const { upstreamC32, upstreamI20, upstreamValue_B51 } = slice;
  if (upstreamC32 === MANUAL_MODE_TOKEN) {
    if (isNonEmptyString(upstreamI20)) {
      return upstreamI20.substring(upstreamI20.length - 1); // RIGHT(I20,1)
    }
    return "";
  }
  if (!Number.isInteger(upstreamValue_B51)) {
    throw new TypeError(
      "[Batch04·B54] upstreamValue_B51 must be an integer in the automatic branch"
    );
  }
  return vlookupSheet39WithIferror(upstreamValue_B51, "e", sheet39Ref);
}

/**
 * Sheet27!B55 = B52 & B54 — deterministic string concatenation.
 */
export function computeB55(B52, B54) {
  return String(B52) + String(B54);
}

/**
 * Aggregate entry point for Batch 04.
 * @param {Object} slice
 * @returns {{B51:number, B52:string|number, B53:string|number, B54:string|number, B55:string, _provenance:Object}}
 */
export function computeBatch04Sheet27(slice) {
  if (slice === null || typeof slice !== "object") {
    throw new TypeError("[Batch04] slice must be an object");
  }
  const sheet39Ref = slice.sheet39Ref || PARTIAL_SHEET39_REFERENCE_EVIDENCE;
  const B51 = computeB51({ ...slice, sheet39Ref });
  const sliceWithB51 = { ...slice, sheet39Ref, upstreamValue_B51: B51 };
  const B52 = computeB52(sliceWithB51);
  const B53 = computeB53(sliceWithB51);
  const B54 = computeB54(sliceWithB51);
  const B55 = computeB55(B52, B54);
  return {
    B51,
    B52,
    B53,
    B54,
    B55,
    _provenance: {
      excelSources: [
        "Sheet27!B51",
        "Sheet27!B52",
        "Sheet27!B53",
        "Sheet27!B54",
        "Sheet27!B55",
      ],
      formulas: {
        "Sheet27!B51":
          "=IF(Sheet39!$B$65<><guard-token>,<error/status-text>,MOD($B$14-Sheet39!$B$63,60)+1)",
        "Sheet27!B52":
          "=IF('01_个人资料'!$C$32=\"手动覆盖\"," +
          "IF('01_个人资料'!$I$20<>\"\",LEFT('01_个人资料'!$I$20,1)," +
          "IF('01_个人资料'!$I$22<>\"\",'01_个人资料'!$I$22,\"\")))," +
          "IFERROR(VLOOKUP(B51,Sheet39!$A:$F,3,FALSE),\"\"))",
        "Sheet27!B53": "=IFERROR(VLOOKUP(B51,Sheet39!$A:$F,4,FALSE),\"\")",
        "Sheet27!B54":
          "=IF('01_个人资料'!$C$32=\"手动覆盖\"," +
          "IF('01_个人资料'!$I$20<>\"\",RIGHT('01_个人资料'!$I$20,1),\"\")," +
          "IFERROR(VLOOKUP(B51,Sheet39!$A:$F,5,FALSE),\"\"))",
        "Sheet27!B55": "=B52&B54",
      },
      lookupSource: {
        worksheetIndex: 39,
        codeName: "Sheet39",
        workbookSheet: "DB_60甲子",
      },
      auditedCells: {
        "Sheet39!B63": 30713,
        "Sheet39!B65": {
          value: B51_GUARD_TOKEN,
          codePoints: [0x5DF2, 0x9A8C, 0x8BC1, 0x0020, 0x2705],
          ascwEvidence: [24050, -25972, -29759, 32, 9989],
        },
      },
      auditedRow: {
        "Sheet39!A29:F29": { a: 28, c: "\u8F9B", d: 8, e: "\u536F" },
      },
      partialReferenceEvidence: PARTIAL_SHEET39_REFERENCE_EVIDENCE.status,
      manualModeToken: MANUAL_MODE_TOKEN, // reused from Batch 03 evidence closure
      note:
        "Business meaning of B51/B52/B53/B54/B55, of the Sheet39!B65 guard " +
        "token, and of Sheet39 lookup values is intentionally NOT labelled. " +
        "'DB_60甲子' is the audited workbook sheet name (raw evidence), NOT " +
        "an interpretation of the values it contains.",
    },
  };
}
